import { Toasts, useModalContext } from '@fourthwall/components'

import type { WebhookType, WebhooksListData } from '../Webhooks.types'
import { ConfigureWebhookModal } from '../components/ConfigureWebhookModal/ConfigureWebhookModal'
import type { Values as ConfigureWebhookModalValues } from '../components/ConfigureWebhookModal/ConfigureWebhookModal.types'
import { useCreateWebhookConfigurationMutation } from '../queries/useCreateWebhookConfigurationMutation'
import { useDeleteWebhookConfigurationMutation } from '../queries/useDeleteWebhookConfigurationMutation'
import { useTestWebhookConfigurationMutation } from '../queries/useTestWebhookConfigurationMutation'
import { useUpdateWebhookConfigurationMutation } from '../queries/useUpdateWebhookConfigurationMutation'
import { useWebhookConfigurationsQuery } from '../queries/useWebhookConfigurationsQuery'
import { useWebhookTypesQuery } from '../queries/useWebhookTypesQuery'
import { getWebhookTypeLabel } from '../utils/getWebhookTypeLabel'

export const useWebhooks = () => {
  const { open } = useModalContext()
  const { webhookConfigurationsQuery, invalidateWebhookConfigurationsQuery } =
    useWebhookConfigurationsQuery()
  const { webhookTypesQuery } = useWebhookTypesQuery()
  const { createWebhookConfigurationMutation } = useCreateWebhookConfigurationMutation()
  const { updateWebhookConfigurationMutation } = useUpdateWebhookConfigurationMutation()
  const { deleteWebhookConfigurationMutation } = useDeleteWebhookConfigurationMutation()
  const { testWebhookConfigurationMutation } = useTestWebhookConfigurationMutation()

  const webhookTypes =
    webhookTypesQuery.data?.types.map((webhookType) => ({
      ...webhookType,
      label: getWebhookTypeLabel(webhookType.type),
    })) || []

  const webhooks: WebhooksListData =
    webhookConfigurationsQuery.data?.configurations.map((webhook) => ({
      ...webhook,
      allowedTypes: webhook.allowedTypes.map((type) => ({
        type,
        label: getWebhookTypeLabel(type),
      })),
      onUpdate: () => onWebhookUpdate(webhook.id),
      onRemove: () => onWebhookRemove(webhook.id),
      onTest: (webhookId: string, webhookType: WebhookType) =>
        onWebhookTest(webhookId, webhookType),
    })) || []

  const onWebhookCreate = () => {
    open(ConfigureWebhookModal, {
      eventOptions: webhookTypes.map(({ type, label }) => ({
        value: type,
        label,
      })),
      onSubmit: (values: ConfigureWebhookModalValues) => {
        createWebhookConfigurationMutation.mutate(
          [
            {
              url: values.url,
              allowedTypes: values.events as WebhookType[],
              apiVersion: 'V1',
            },
          ],
          {
            onSuccess: () => {
              invalidateWebhookConfigurationsQuery()
            },
          },
        )
      },
    })
  }

  const onWebhookUpdate = (webhookId: string) => {
    const webhook = webhooks.find(({ id }) => id === webhookId)

    open(ConfigureWebhookModal, {
      initialValues: {
        url: webhook?.url || '',
        events: webhook?.allowedTypes.map(({ type }) => type) || [],
      },
      eventOptions: webhookTypes.map(({ type, label }) => ({
        value: type,
        label,
      })),
      onSubmit: (values: ConfigureWebhookModalValues) => {
        updateWebhookConfigurationMutation.mutate(
          [
            {
              webhookConfigurationId: webhookId,
            },
            {
              url: values.url,
              allowedTypes: values.events as WebhookType[],
              apiVersion: 'V1',
            },
          ],
          {
            onSuccess: () => {
              invalidateWebhookConfigurationsQuery()
            },
          },
        )
      },
    })
  }

  const onWebhookRemove = (webhookId: string) => {
    deleteWebhookConfigurationMutation.mutate([{ webhookConfigurationId: webhookId }], {
      onSuccess: () => {
        invalidateWebhookConfigurationsQuery()
      },
    })
  }

  const onWebhookTest = (webhookId: string, webhookType: WebhookType) => {
    testWebhookConfigurationMutation.mutate(
      [
        {
          webhookConfigurationId: webhookId,
        },
        {
          type: webhookType,
        },
      ],
      {
        onSuccess: () => {
          Toasts.notify('Test notification has been sent', { type: 'success' })
        },
      },
    )
  }

  return {
    webhooks,
    webhookTypes,
    isLoading: webhookConfigurationsQuery.isLoading,
    clientSecret: webhookConfigurationsQuery.data?.secret?.value,
    onWebhookCreate: webhookTypesQuery.isFetched ? onWebhookCreate : undefined,
    onWebhookUpdate,
    onWebhookRemove,
    onWebhookTest,
  }
}
